import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Mail } from "lucide-react";
import api, { errText, qrImage } from "@/lib/api";
import { Shell } from "@/components/Shell";

export default function RetrieveQR() {
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const search = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResults(null);
    try {
      const payload = {};
      if (form.email) payload.email = form.email;
      if (form.first_name) payload.first_name = form.first_name;
      if (form.last_name) payload.last_name = form.last_name;
      const { data } = await api.post("/public/lookup", payload);
      setResults(data);
      toast.success(`${data.length} pass(es) found`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (token) => {
    try {
      const { data } = await api.post(`/public/registrations/${token}/resend`);
      toast.success(`Pass re-sent to ${data.to}`);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <Shell>
      <div className="rise" data-testid="retrieve-page">
        <p className="overline">Self-service</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mt-2">
          Find my QR pass
        </h1>
        <p className="text-sm text-slate-400 mt-4 max-w-lg">
          Enter the email you registered with, or your first and last name. Your pass appears instantly on
          screen — and you can resend it to yourself.
        </p>

        <form onSubmit={search} className="card p-6 mt-8 max-w-xl" data-testid="retrieve-form">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" data-testid="retrieve-email" value={form.email} onChange={set("email")} />
          </div>
          <p className="text-center text-[11px] font-mono2 text-slate-500 my-4">— or —</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">First name</label>
              <input className="input" data-testid="retrieve-first-name" value={form.first_name} onChange={set("first_name")} />
            </div>
            <div>
              <label className="label">Last name</label>
              <input className="input" data-testid="retrieve-last-name" value={form.last_name} onChange={set("last_name")} />
            </div>
          </div>
          <button className="btn-primary w-full mt-6" disabled={busy} data-testid="retrieve-submit-btn">
            <Search size={15} /> {busy ? "Searching…" : "Retrieve my pass"}
          </button>
        </form>

        {results && (
          <div className="grid sm:grid-cols-2 gap-5 mt-10" data-testid="retrieve-results">
            {results.map(({ registration: r, event }) => (
              <div key={r.registration_id} className="card p-5 flex gap-4" data-testid={`retrieve-result-${r.registration_code}`}>
                <div className="bg-white rounded-lg p-2 h-fit">
                  <img src={qrImage(r.qr_token)} alt="qr" className="w-24 h-24" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold leading-snug truncate">{event?.name}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {event?.date} · {r.status}
                  </p>
                  <p className="font-mono2 text-xs text-slate-500 mt-1">REF {r.registration_code}</p>
                  <div className="flex gap-3 mt-3">
                    <Link to={`/ticket/${r.qr_token}`} className="btn-ghost text-xs">
                      Open pass
                    </Link>
                    <button onClick={() => resend(r.qr_token)} className="btn-ghost text-xs" data-testid={`retrieve-resend-${r.registration_code}`}>
                      <Mail size={13} /> Resend
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
